import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const Projects = () => {
  const projects = [
    {
      name: "KaanchDrive – Secure Cloud Storage",
      description: "Developed secure cloud storage platform using React.js, Tailwind CSS, and Pinata/IPFS for decentralized file storage. Integrated Kaanch Wallet for non-custodial, blockchain-based authentication. Prioritized encrypted file handling and privacy-first user experience.",
      techStack: ["React.js", "Tailwind CSS", "IPFS", "Blockchain"],
      link: "https://kaanchdrive.com",
      image: "https://pub-f76b87a889044eea986677ba77f1ce3e.r2.dev/Screenshot%202025-05-16%20215546.png"
    },
    {
      name:"HOTEL BOOKING APP",
      description: "A hotel booking app to search, compare, and book rooms instantly with secure payments, real-time availability, and personalized offers.",
      techStack: ["MongoDB", "Express.js", "React.js","Node.js"],
      link: "https://hb-gs.vercel.app/",
      image: "https://pub-f76b87a889044eea986677ba77f1ce3e.r2.dev/Screenshot%202025-05-16%20214917.png"
    },
    {
      name: "Rehome Pet Adoption Platform",
      description: "A full-stack pet adoption app enabling users to list, adopt, and donate to pets in need, featuring secure authentication and donation processing.",
      techStack: ["React Native", "Expo", "REST APIs"],
      link: "https://rehome.pages.dev/",
      image: "https://pub-f76b87a889044eea986677ba77f1ce3e.r2.dev/Screenshot%202025-04-12%20180551.png"
    },
     {
      name: "Movie Reviewing Web App",
      description: "A movie review platform integrated with the TMDB API, allowing users to search, rate, and review movies.",
      techStack: ["React", "TMDB API"],
      link: "https://test-9si.pages.dev/",
      image: "https://pub-f76b87a889044eea986677ba77f1ce3e.r2.dev/Screenshot%202025-04-12%20180638.png"
    },
    {
      name: "Music Player Web App",
      description: "A web-based music player with play, pause, and playlist features, ensuring responsive design across devices.",
      techStack: ["HTML", "CSS", "JavaScript"],
      link: "https://music-cth.pages.dev/",
      image: "https://pub-f76b87a889044eea986677ba77f1ce3e.r2.dev/Screenshot%202025-05-17%20001308.png"
    },
    {
      name: "Note-Taking Web App",
      description: "A browser-based note-taking app with save and search capabilities, utilizing local storage for data retention.",
      techStack: ["HTML", "CSS", "JavaScript"],
      link: "https://note--taking--app.pages.dev/",
      image: "https://pub-f76b87a889044eea986677ba77f1ce3e.r2.dev/Screenshot%202025-04-12%20180727.png"
    },
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -100, y: 50, rotate: -5 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      rotate: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
        type: "spring",
        stiffness: 50
      }
    }
  };

  const tagVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 0.2
      }
    }
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: {
      scale: 1.1,
      boxShadow: "0 0 15px rgba(255, 255, 255, 0.6), 0 0 30px rgba(59, 130, 246, 0.4)",
      transition: {
        duration: 0.3
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.1 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    }
  };

  const swipeTextVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.1,
        ease: "easeOut",
        repeat: Infinity,
        repeatType: "reverse",
        repeatDelay: 1
      }
    }
  };

  return (
    <section
      id="project"
      className="scroll-reveal"
      ref={ref}
      style={{
        backgroundColor: '#000000',
        padding: '60px 15px',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.2) 10%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(147, 51, 234, 0.2) 10%, transparent 50%)',
          animation: 'nebula 20s infinite linear',
          zIndex: 0
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'transparent',
          zIndex: 1
        }}
        className="starfield"
      />
      <section id="projects" className="projects-section" style={{ position: 'relative', zIndex: 2 }}>
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              marginBottom: '1rem',
              color: '#ffffff',
              textShadow: '0 0 10px rgba(255, 255, 255, 0.7)',
              position: 'relative'
            }}
          >
            Projects
            <span
              style={{
                position: 'absolute',
                top: '-15px',
                left: '50%',
                width: '80px',
                height: '80px',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
                animation: 'constellationPulse 5s infinite',
                transform: 'translateX(-50%)'
              }}
            />
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              fontSize: '1.1rem',
              color: '#d1d5db',
              maxWidth: '800px',
              margin: '0 auto 2rem',
              lineHeight: '1.6'
            }}
          >
            The following projects showcase my skills and experience through real-world examples of my work. Each project is briefly described with links to code repositories and live demos. They reflect my ability to solve complex problems, work with different technologies, and manage projects effectively.
          </motion.p>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            style={{
              marginBottom: '1rem',
              display: 'none'
            }}
            className="swipe-instruction"
          >
            <motion.span
              variants={swipeTextVariants}
              style={{
                fontSize: '0.9rem',
                color: '#d1d5db',
                fontWeight: '500',
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '9999px'
              }}
            >
              Swipe to explore
            </motion.span>
          </motion.div>
          <motion.div
            className="projects-slider"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            style={{
              display: 'flex',
              gap: '1rem',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              padding: '0 10px 1.5rem',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {projects.map((project, index) => (
              <motion.div
                key={index}
                className="project-card"
                variants={cardVariants}
                whileHover={{
                  scale: 1.03,
                  boxShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
                  rotate: 1
                }}
                style={{
                  flex: '0 0 85%',
                  minWidth: '250px',
                  maxWidth: '320px',
                  backgroundColor: 'rgba(26, 26, 26, 0.9)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  scrollSnapAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <motion.div
                  className="stardust"
                  whileHover={{
                    opacity: 1,
                    transition: { duration: 0.2 }
                  }}
                  initial={{ opacity: 0 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.5) 2%, transparent 10%)',
                    pointerEvents: 'none'
                  }}
                />
                <motion.img
                  src={project.image}
                  alt={`${project.name} preview`}
                  variants={imageVariants}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '0.8rem',
                    marginBottom: '0.8rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                />
                <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#ffffff' }}>{project.name}</h3>
                <p style={{ margin: '10px 0', color: '#d1d5db', fontSize: '0.9rem' }}>{project.description}</p>
                <div style={{ marginTop: '10px', marginBottom: '15px', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {project.techStack.map((tech, idx) => (
                    <motion.span
                      key={idx}
                      variants={tagVariants}
                      whileHover={{
                        scale: 1.2,
                        boxShadow: '0 0 10px rgba(0, 255, 204, 0.7)',
                        background: 'rgba(0, 255, 204, 0.2)'
                      }}
                      style={{
                        display: 'inline-block',
                        backgroundColor: 'transparent',
                        color: '#00ffcc',
                        fontSize: '0.7rem',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '9999px',
                        fontWeight: '500',
                        border: '1px solid #00ffcc',
                        animation: 'cometGlow 2.5s infinite'
                      }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
                <motion.a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={buttonVariants}
                  initial="rest"
                  whileHover="hover"
                  style={{
                    display: 'inline-block',
                    padding: '0.6rem 1.2rem',
                    background: 'linear-gradient(45deg, #3b82f6, #60a5fa)',
                    color: '#ffffff',
                    borderRadius: '9999px',
                    fontWeight: '600',
                    fontSize: '0.8rem',
                    textDecoration: 'none',
                    position: 'relative',
                    animation: 'warpPulse 3s infinite'
                  }}
                >
                  🔗 View Project
                  <motion.span
                    whileHover={{
                      x: [0, 30, 0],
                      opacity: [0, 1, 0],
                      transition: { duration: 0.5, repeat: Infinity }
                    }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '100%',
                      width: '15px',
                      height: '2px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      transform: 'translateY(-50%)'
                    }}
                  />
                </motion.a>
              </motion.div>
            ))}
          </motion.div>
          <motion.div
            className="projects-grid-desktop"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            style={{
              display: 'none',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
              marginTop: '3rem',
            }}
          >
            {projects.map((project, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 0 30px rgba(255, 255, 255, 0.4)',
                  rotate: 2
                }}
                style={{
                  backgroundColor: 'rgba(26, 26, 26, 0.9)',
                  borderRadius: '1.5rem',
                  padding: '2rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <motion.div
                  className="stardust"
                  whileHover={{
                    opacity: 1,
                    transition: { duration: 0.2 }
                  }}
                  initial={{ opacity: 0 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.5) 2%, transparent 10%)',
                    pointerEvents: 'none'
                  }}
                />
                <motion.img
                  src={project.image}
                  alt={`${project.name} preview`}
                  variants={imageVariants}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    marginBottom: '1rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                />
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>{project.name}</h3>
                <p style={{ margin: '15px 0', color: '#d1d5db' }}>{project.description}</p>
                <div style={{ marginTop: '15px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {project.techStack.map((tech, idx) => (
                    <motion.span
                      key={idx}
                      variants={tagVariants}
                      whileHover={{
                        scale: 1.3,
                        boxShadow: '0 0 15px rgba(0, 255, 204, 0.8)',
                        background: 'rgba(0, 255, 204, 0.2)'
                      }}
                      style={{
                        display: 'inline-block',
                        backgroundColor: 'transparent',
                        color: '#00ffcc',
                        fontSize: '0.8rem',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '9999px',
                        fontWeight: '500',
                        border: '1px solid #00ffcc',
                        animation: 'cometGlow 2.5s infinite'
                      }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
                <motion.a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={buttonVariants}
                  initial="rest"
                  whileHover="hover"
                  style={{
                    display: 'inline-block',
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(45deg, #3b82f6, #60a5fa)',
                    color: '#ffffff',
                    borderRadius: '9999px',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    position: 'relative',
                    animation: 'warpPulse 3s infinite'
                  }}
                >
                  🔗 View Project
                  <motion.span
                    whileHover={{
                      x: [0, 50, 0],
                      opacity: [0, 1, 0],
                      transition: { duration: 0.5, repeat: Infinity }
                    }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '100%',
                      width: '20px',
                      height: '2px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      transform: 'translateY(-50%)'
                    }}
                  />
                </motion.a>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      <style jsx>{`
        .projects-slider::-webkit-scrollbar {
          display: none;
        }
        @keyframes nebula {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
          50% { transform: translate(30px, -30px) scale(1.05); opacity: 0.4; }
        }
        @keyframes cometGlow {
          0%, 100% { box-shadow: 0 0 5px rgba(0, 255, 204, 0.4); }
          50% { box-shadow: 0 0 15px rgba(0, 255, 204, 0.7); }
        }
        @keyframes warpPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(59, 130, 246, 0.4); }
          50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.7); }
        }
        @keyframes constellationPulse {
          0%, 100% { opacity: 0.2; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.4; transform: translateX(-50%) scale(1.1); }
        }
        .starfield {
          background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"><g fill="rgba(255,255,255,0.7)">${Array.from({ length: 50 }).map((_, i) => `<circle cx="${Math.random() * 100}%" cy="${Math.random() * 100}%" r="${Math.random() * 1.2}" style="animation: twinkle ${2 + Math.random() * 3}s infinite ${Math.random() * 2}s"/>`).join('')}</g></svg>') no-repeat;
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        .project-card:hover .stardust {
          background: radial-gradient(circle, rgba(255, 255, 255, 0.7) 2%, transparent 10%);
          animation: sparkle 0.5s infinite;
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @media (min-width: 768px) {
          .projects-slider { display: none !important; }
          .projects-grid-desktop { display: grid !important; }
          .swipe-instruction { display: none !important; }
        }
        @media (max-width: 767px) {
          .swipe-instruction { display: block !important; }
          .projects-slider {
            padding: 0 5px 1rem;
            gap: 0.8rem;
          }
          .project-card {
            flex: 0 0 90%;
            min-width: 220px;
            maxWidth: 300px;
            padding: 1.2rem;
            border-radius: 0.8rem;
          }
          .projects-section h2 {
            font-size: 2rem;
            margin-bottom: 1.5rem;
          }
          .project-card img {
            height: 130px;
            border-radius: 0.6rem;
          }
          .project-card h3 {
            font-size: 1.1rem;
          }
          .project-card p {
            font-size: 0.85rem;
            line-height: 1.4;
          }
          .project-card a {
            padding: 0.5rem 1rem;
            font-size: 0.75rem;
          }
        }
        @media (max-width: 480px) {
          .project-card {
            flex: 0 0 95%;
            min-width: 200px;
            max-width: 280px;
            padding: 1rem;
          }
          .projects-section h2 {
            font-size: 1.8rem;
          }
          .project-card img {
            height: 120px;
          }
          .project-card h3 {
            font-size: 1rem;
          }
          .project-card p {
            font-size: 0.8rem;
          }
          .project-card a {
            padding: 0.4rem 0.8rem;
            font-size: 0.7rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;